import React, { useState, useRef, useEffect } from 'react';
import {
  FiStar,
  FiCheckCircle,
  FiArrowRight,
  FiMessageCircle,
} from 'react-icons/fi';
import './Recipes.css';

/* ─── BRANDS DATA ─────────────────────────────────────────────── */
// eslint-disable-next-line no-unused-vars
const brandsData = [
  { name: 'Bon Appétit', logo: 'https://via.placeholder.com/180x60/1a4f0c/ffffff?text=Bon+Appetit', color: '#e8472a' },
  { name: 'Food Network', logo: 'https://via.placeholder.com/180x60/fabc2a/1a4f0c?text=Food+Network', color: '#fabc2a' },
  { name: 'The Kitchn', logo: 'https://via.placeholder.com/180x60/1a4f0c/ffffff?text=The+Kitchn', color: '#1a4f0c' },
  { name: 'Saveur Magazine', logo: 'https://via.placeholder.com/180x60/c94b0a/ffffff?text=Saveur', color: '#c94b0a' },
  { name: 'Serious Eats', logo: 'https://via.placeholder.com/180x60/2d5a1b/ffffff?text=Serious+Eats', color: '#2d5a1b' },
  { name: 'Epicurious', logo: 'https://via.placeholder.com/180x60/d4780a/ffffff?text=Epicurious', color: '#d4780a' },
  { name: 'Cooking Light', logo: 'https://via.placeholder.com/180x60/1a4f0c/ffffff?text=Cooking+Light', color: '#1a4f0c' },
  { name: 'Taste of Home', logo: 'https://via.placeholder.com/180x60/fabc2a/1a4f0c?text=Taste+of+Home', color: '#fabc2a' },
];

/* ─── TESTIMONIALS DATA ─────────────────────────────────────────────── */
const testimonialsData = [
  {
    id: 1,
    text: "The saffron from Shammy Spices transformed my risotto completely. You can actually taste the difference - it's like night and day compared to supermarket brands.",
    author: 'Sarah Martinez',
    role: 'Home Chef',
    location: 'San Francisco, CA',
    rating: 5,
    initials: 'SM',
    color: '#1a4f0c',
    spice: 'Kashmiri Saffron',
  },
  {
    id: 2,
    text: "I've been a professional chef for 15 years, and I can confidently say Shammy Spices are the real deal. The smoked paprika has incredible depth.",
    author: 'Marcus Chen',
    role: 'Executive Chef',
    location: 'New York, NY',
    rating: 5,
    initials: 'MC',
    color: '#2d7a1c',
    spice: 'Smoked Paprika',
  },
  {
    id: 3,
    text: "Finally found a spice company that sources ethically AND delivers exceptional quality. The black cardamom is absolutely incredible in my biryanis!",
    author: 'Priya Kapoor',
    role: 'Food Blogger',
    location: 'London, UK',
    rating: 5,
    initials: 'PK',
    color: '#c94b0a',
    spice: 'Black Cardamom',
  },
  {
    id: 4,
    text: "Every single spice arrives fresh and aromatic. The difference is immediate - my customers notice it in every dish I serve.",
    author: 'David Thompson',
    role: 'Restaurant Owner',
    location: 'Austin, TX',
    rating: 5,
    initials: 'DT',
    color: '#fabc2a',
    spice: 'Tellicherry Pepper',
  },
];

/* ─── SUB-COMPONENTS ────────────────────────────────────────── */
// eslint-disable-next-line no-unused-vars
const BrandsGrid = ({ brands }) => {
  return (
    <div className="brands-grid-wrapper">
      {brands.map((brand, index) => (
        <div
          key={brand.name}
          className="brand-item"
          style={{ '--index': index }}
        >
          <div className="brand-card-new">
            <img src={brand.logo} alt={brand.name} />
          </div>
        </div>
      ))}
    </div>
  );
};

const TestimonialCarousel = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 4500);

    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const handleNext = () => {
    if (!isTransitioning) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % testimonials.length);
        setIsTransitioning(false);
      }, 600);
    }
  };

  const handleDotClick = (index) => {
    if (!isTransitioning && index !== currentIndex) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentIndex(index);
        setIsTransitioning(false);
      }, 600);
    }
  };

  const getVisibleTestimonials = () => {
    const total = testimonials.length;
    return [
      testimonials[currentIndex],
      testimonials[(currentIndex + 1) % total],
      testimonials[(currentIndex + 2) % total],
    ];
  };

  const visibleCards = getVisibleTestimonials();

  return (
    <div className="testimonial-carousel">
      <div className="testimonial-stack">
        {/* Back card (position 2) */}
        <div className={`testimonial-card-stacked card-position-2 ${isTransitioning ? 'transitioning' : ''}`}>
          <div className="testimonial-quote-icon">
            <FiMessageCircle />
          </div>
          <div className="testimonial-stars">
            {[...Array(5)].map((_, i) => (
              <FiStar key={i} fill="#fabc2a" color="#fabc2a" />
            ))}
          </div>
          <p className="testimonial-text">"{visibleCards[2].text}"</p>
          <div className="testimonial-divider" />
          <div className="testimonial-author-section">
            <div className="testimonial-avatar" style={{ background: visibleCards[2].color }}>{visibleCards[2].initials}</div>
            <div className="testimonial-author-info">
              <h4 className="testimonial-author">{visibleCards[2].author}</h4>
              <p className="testimonial-role">{visibleCards[2].role}</p>
              <p className="testimonial-location">{visibleCards[2].location}</p>
            </div>
          </div>
          <div className="testimonial-verified">
            <FiCheckCircle />
            <span>Verified Purchase: {visibleCards[2].spice}</span>
          </div>
        </div>

        {/* Middle card (position 1) */}
        <div className={`testimonial-card-stacked card-position-1 ${isTransitioning ? 'transitioning' : ''}`}>
          <div className="testimonial-quote-icon">
            <FiMessageCircle />
          </div>
          <div className="testimonial-stars">
            {[...Array(5)].map((_, i) => (
              <FiStar key={i} fill="#fabc2a" color="#fabc2a" />
            ))}
          </div>
          <p className="testimonial-text">"{visibleCards[1].text}"</p>
          <div className="testimonial-divider" />
          <div className="testimonial-author-section">
            <div className="testimonial-avatar" style={{ background: visibleCards[1].color }}>{visibleCards[1].initials}</div>
            <div className="testimonial-author-info">
              <h4 className="testimonial-author">{visibleCards[1].author}</h4>
              <p className="testimonial-role">{visibleCards[1].role}</p>
              <p className="testimonial-location">{visibleCards[1].location}</p>
            </div>
          </div>
          <div className="testimonial-verified">
            <FiCheckCircle />
            <span>Verified Purchase: {visibleCards[1].spice}</span>
          </div>
        </div>

        {/* Front card (position 0) */}
        <div className={`testimonial-card-stacked card-position-0 ${isTransitioning ? 'transitioning' : ''}`}>
          <div className="testimonial-quote-icon">
            <FiMessageCircle />
          </div>
          <div className="testimonial-stars">
            {[...Array(5)].map((_, i) => (
              <FiStar key={i} fill="#fabc2a" color="#fabc2a" />
            ))}
          </div>
          <p className="testimonial-text">"{visibleCards[0].text}"</p>
          <div className="testimonial-divider" />
          <div className="testimonial-author-section">
            <div className="testimonial-avatar" style={{ background: visibleCards[0].color }}>{visibleCards[0].initials}</div>
            <div className="testimonial-author-info">
              <h4 className="testimonial-author">{visibleCards[0].author}</h4>
              <p className="testimonial-role">{visibleCards[0].role}</p>
              <p className="testimonial-location">{visibleCards[0].location}</p>
            </div>
          </div>
          <div className="testimonial-verified">
            <FiCheckCircle />
            <span>Verified Purchase: {visibleCards[0].spice}</span>
          </div>
        </div>
      </div>

      <div className="carousel-dots">
        {testimonials.map((_, index) => (
          <button
            key={index}
            className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => handleDotClick(index)}
            aria-label={`Go to testimonial ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

/* ─── MAIN COMPONENT ────────────────────────────────────────── */
const Recipes = () => {
  const brandsRef = useRef(null);
  const testimonialsRef = useRef(null);
  const [sectionsVisible, setSectionsVisible] = useState({
    brands: false,
    testimonials: false,
  });

  useEffect(() => {
    const observers = {
      brands: new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setSectionsVisible(prev => ({ ...prev, brands: true }));
            observers.brands.disconnect();
          }
        },
        { threshold: 0.1 }
      ),
      testimonials: new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setSectionsVisible(prev => ({ ...prev, testimonials: true }));
            observers.testimonials.disconnect();
          }
        },
        { threshold: 0.1 }
      ),
    };

    if (brandsRef.current) observers.brands.observe(brandsRef.current);
    if (testimonialsRef.current) observers.testimonials.observe(testimonialsRef.current);

    return () => {
      observers.brands.disconnect();
      observers.testimonials.disconnect();
    };
  }, []);

  return (
    <div className="recipes-wrapper">
      {/* ═══ TESTIMONIALS SECTION ═══ */}
      <section
        id="testimonials"
        ref={testimonialsRef}
        className={`testimonials-section ${sectionsVisible.testimonials ? 'in-view' : ''}`}
      >
        <div className="section-bg-testimonials" aria-hidden="true">
          <div className="bg-blob-testimonials bg-blob-testimonials--1" />
          <div className="bg-blob-testimonials bg-blob-testimonials--2" />
        </div>

        <div className="testimonials-container">
          <header className="testimonials-header">
            <div className="header-eyebrow-testimonials">
              <span className="eyebrow-line-testimonials" />
              <span className="eyebrow-text-testimonials">What Customers Say</span>
              <span className="eyebrow-line-testimonials" />
            </div>

            <h2 className="testimonials-title">
              Loved by <span className="testimonials-title-accent">Chefs &amp; Home Cooks</span>
            </h2>

            <div className="testimonials-stats-banner">
              <div className="stats-banner-item">
                <strong>4.9/5</strong>
                <span>Average Rating</span>
              </div>
              <div className="stats-banner-divider" />
              <div className="stats-banner-item">
                <strong>15,000+</strong>
                <span>5-Star Reviews</span>
              </div>
              <div className="stats-banner-divider" />
              <div className="stats-banner-item">
                <strong>98%</strong>
                <span>Would Recommend</span>
              </div>
            </div>
          </header>

          <TestimonialCarousel testimonials={testimonialsData} />

          <div className="testimonials-footer-cta">
            <a href="#reviews" className="view-reviews-btn">
              <span>Read All Reviews</span>
              <FiArrowRight />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Recipes;